import { parsePhoneNumberFromString } from "libphonenumber-js/max";

const DEFAULT_COUNTRY_CODE = "IL";

/**
 * Parses a phone string into a libphonenumber-js PhoneNumber.
 *
 * Validation policy is intentionally lenient: we only require the number to
 * be structurally parseable and pass `.isPossible()` (the length / general
 * shape check). We do NOT call `.isValid()`, which goes against country
 * metadata to verify the number's prefix is in an allocated range.
 *
 * Why lenient: `.isValid()` rejects real, dialable numbers whenever the
 * bundled metadata lags behind reality. The recurring case here is Israeli
 * MVNO mobile prefixes — e.g. 055-* (`+972554557049`) — which scraped
 * listings and customer-supplied contacts both contain. Rejecting them
 * caused phones to render as raw `+9725…` strings instead of formatted
 * local numbers, and blocked them from features that key off a parsed
 * country / national format. Format compliance is enough for our needs;
 * the carrier will sort out whether the number actually rings.
 */
export function parsePhoneNumber(phoneNumber: string) {
  if (typeof phoneNumber !== "string") {
    phoneNumber = (phoneNumber as any)?.toString();
  }
  if (!phoneNumber) {
    return null;
  }

  var phoneNumberObj = parsePhoneNumberFromString(phoneNumber, DEFAULT_COUNTRY_CODE);

  if (!phoneNumberObj) {
    // Common DB quirk: intl numbers are stored without the leading "+".
    // Re-try with one prepended before giving up.
    phoneNumber = `+${phoneNumber}`;
    phoneNumberObj = parsePhoneNumberFromString(phoneNumber, DEFAULT_COUNTRY_CODE);
    if (!phoneNumberObj) {
      return null;
    }
  } else if (!phoneNumberObj.isValid() && !phoneNumber.startsWith("+")) {
    // Digits-only international numbers (e.g. "33680410732", a French mobile
    // stored without its "+") parse under the IL default as a garbage-but-
    // "possible" Israeli number (+97233680410732). If re-parsing with a "+"
    // yields a VALID number, prefer it. Invalid-but-possible IL numbers
    // (055-* MVNOs) are unaffected: their "+" parse is never valid.
    const intlObj = parsePhoneNumberFromString(`+${phoneNumber}`, DEFAULT_COUNTRY_CODE);
    if (intlObj?.isValid()) {
      phoneNumberObj = intlObj;
    }
  }

  if (!phoneNumberObj.isPossible()) {
    return null;
  }

  return phoneNumberObj;
}

/**
 * Fallback for Israeli phone numbers that libphonenumber-js rejects (e.g., 055-* MVNO numbers).
 * Strips dashes/spaces and prepends +972, removing the leading 0.
 */
function formatIsraeliPhoneFallback(phone: string): string | null {
  const digits = phone.replace(/[-\s()]/g, "");
  if (digits.startsWith("0") && digits.length === 10) {
    return `+972${digits.slice(1)}`;
  }
  return null;
}

// Will return something like +972527485568
export function toInternationalFormat(phoneNumber?: string) {
  const phoneNumberObj = parsePhoneNumber(phoneNumber);
  if (!phoneNumberObj) {
    // Fallback: manually format Israeli numbers that libphonenumber-js doesn't recognize (e.g., 055-*)
    if (phoneNumber) {
      const fallback = formatIsraeliPhoneFallback(phoneNumber);
      if (fallback) {
        console.log(`Used Israeli phone fallback for: ${phoneNumber} -> ${fallback}`);
        return fallback;
      }
    }
    console.warn("Could not convert to international format:", phoneNumber);
    return null;
  }

  return phoneNumberObj.format("E.164");
}

// TODO - this is pending a backend change, where we will save all phone numbers as international numbers
export function getLocalPhoneNumberIfIsraeliNumber(phoneNumberStr, removeNonDigits = true) {
  var phoneNumberObj = parsePhoneNumber(phoneNumberStr);

  if (!phoneNumberObj || !phoneNumberObj.country) {
    return null;
  }

  if (phoneNumberObj.country != "IL") {
    return phoneNumberObj.format("E.164");
  } else {
    return removeNonDigits
      ? phoneNumberObj.formatNational().replace(/\D/g, "")
      : phoneNumberObj.formatNational();
  }
}

/**
 * Digit-only representations of a phone number, for format-agnostic search.
 *
 * Phones are stored in international E.164 form (e.g. "+972545551234"), but a
 * user searching for one types it however they remember it — with dashes,
 * spaces, a leading 0, or local vs. international. To match all of those with a
 * plain substring test, we expand each stored phone into every digit-only form
 * it could be typed as:
 *   - raw stored digits     -> "972545551234"
 *   - international digits   -> "972545551234"
 *   - local Israeli digits   -> "0545551234"
 *
 * A caller strips the query to digits and tests `variant.includes(queryDigits)`
 * against each variant, so partial numbers (e.g. the last 7 digits) also match.
 * De-duplicated; empty/unparseable input yields [].
 */
export function getPhoneSearchVariants(phoneNumber?: string | null): string[] {
  if (!phoneNumber) {
    return [];
  }
  const variants = new Set<string>();
  const raw = String(phoneNumber).replace(/\D/g, "");
  if (raw) {
    variants.add(raw);
  }
  const intl = toInternationalFormat(phoneNumber);
  if (intl) {
    variants.add(intl.replace(/\D/g, ""));
  }
  const local = getLocalPhoneNumberIfIsraeliNumber(phoneNumber, true);
  if (local) {
    variants.add(String(local).replace(/\D/g, ""));
  }
  return [...variants].filter(Boolean);
}

export function isMobile(phoneNumber: string) {
  console.log("Checking if phone number is mobile: ", phoneNumber);
  const phoneNumberInternational = toInternationalFormat(phoneNumber);

  if (!phoneNumberInternational) {
    console.log("Bad phone number");
    return false;
  }

  // Only Israel support FOR NOW (9725)
  return phoneNumberInternational.startsWith(`9725`);
}
